/**
 * Placeholder product tile for the composer preview.
 *
 * The buyer-facing card is spec 26's and lives in `apps/store`, which the ERP
 * may not import (AGENTS §4). This is a visual twin at the same shape — image,
 * stock badge, name, pack line, price row, "Add pack" — so the composer preview
 * reflects the real card's layout (and its equal-height behaviour) without
 * pretending the numbers are real; the caption under the preview says so.
 */
import type { CSSProperties } from "react";

const badgePill: CSSProperties = {
  position: "absolute",
  top: "0.5rem",
  left: "0.5rem",
  fontSize: "0.6875rem",
  fontWeight: 600,
  padding: "0.1875rem 0.5rem",
  borderRadius: "999px",
  lineHeight: 1.2,
  background: "rgba(21,128,61,0.12)",
  color: "#15803d",
};

export function PreviewCard({
  name,
  packLine,
  priceLabel,
  imageUrl,
  imageFallback,
}: {
  name?: string;
  /** e.g. "8-pack · S–XXL"; falls back to the generic "Pack · pcs". */
  packLine?: string;
  /** e.g. "₹2,400 / pack"; falls back to the signed-out "Sign in to view price". */
  priceLabel?: string;
  /** Placeholder art (from sample-images.ts); blank → plain block. */
  imageUrl?: string;
  /** Swapped in via `onError` when `imageUrl` (a hotlinked photo) fails. */
  imageFallback?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: "1px solid var(--line)",
        borderRadius: "var(--r)",
        overflow: "hidden",
        backgroundColor: "var(--paper)",
      }}
    >
      <div style={{ position: "relative", aspectRatio: "4 / 5", backgroundColor: "var(--bg)" }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            onError={(e) => {
              // Offline / CDN failure → the local silhouette, once (guarding
              // against a loop if the fallback itself somehow errors).
              if (imageFallback && e.currentTarget.src !== imageFallback) {
                e.currentTarget.src = imageFallback;
              }
            }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : null}
        <span style={badgePill}>In stock</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, padding: "0.75rem 0.875rem 0.875rem" }}>
        <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "var(--ink)", lineHeight: 1.3 }}>
          {name ?? "Product"}
        </div>
        <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.25rem" }}>
          {packLine ?? "Pack · pcs"}
        </div>
        <div style={{ flex: 1, minHeight: "0.625rem" }} />
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--accent)" }}>
            {priceLabel ?? "Sign in to view price"}
          </span>
          <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "var(--muted)" }}>MOQ</span>
        </div>
        <div
          style={{
            marginTop: "0.6875rem",
            height: "2.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "var(--r-sm)",
            background: "var(--accent)",
            color: "var(--accent-fg)",
            fontWeight: 600,
            fontSize: "0.8125rem",
          }}
        >
          Add pack
        </div>
      </div>
    </div>
  );
}
